/**
 * utils/hireFormat.ts
 * Small, pure presentation helpers shared across SK Hire screens. Keeping these
 * in one place avoids per-screen drift (initials, comp, age, score colour).
 */
import type { ColorScheme } from '../theme/colors';

export function initialsOf(name?: string | null): string {
  return (name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? '').join('').toUpperCase() || '–';
}

/** Days since an ISO timestamp (lists return createdAt, not a precomputed age). */
export function ageDaysFrom(iso?: string | null): number | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor(ms / 86_400_000));
}

function toNum(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Compensation range in LPA (Decimal columns arrive as strings). */
export function fmtComp(min: string | number | null, max: string | number | null): string {
  const lo = toNum(min);
  const hi = toNum(max);
  if (lo == null && hi == null) return '—';
  const lpa = (n: number) => (n / 100000).toFixed(0);
  if (lo != null && hi != null) return `₹${lpa(lo)}–${lpa(hi)}L LPA`;
  return `₹${lpa((lo ?? hi) as number)}L LPA`;
}

/** Candidate.skills is a Json column — coerce to a string[] defensively. */
export function asSkills(skills: unknown): string[] {
  if (Array.isArray(skills)) return skills.filter((s): s is string => typeof s === 'string');
  return [];
}

/** Match-score colour thresholds (≥75 green · ≥55 blue · else amber). */
export function scoreColorKey(pct: number): 'success' | 'info' | 'warn' {
  return pct >= 75 ? 'success' : pct >= 55 ? 'info' : 'warn';
}

export function scoreColor(pct: number, colors: ColorScheme): string {
  return colors[scoreColorKey(pct)];
}

/**
 * The skills (nice-to-have) component of a breakdown string, and nothing else.
 *
 * Every candidate on a match list already cleared the Stage-1 gate, so
 * experience sits inside the band and the title score barely moves — those
 * components print near-identical values on every row while pushing the line
 * into truncation. Skills overlap is the part that actually separates one
 * candidate from the next.
 *
 * Display-only: the server still scores, ranks and stores every component.
 * Falls back to the full string if the expected part is missing, so an
 * unrecognised server format degrades to more detail rather than a blank line.
 */
export function skillsOnlyBreakdown(breakdown: string): string {
  const kept = breakdown
    .split('·')
    .map((part) => part.trim())
    .filter((part) => /^nice[- ]?to[- ]?have\b/i.test(part));
  return kept.length ? kept.join(' · ') : breakdown;
}
